/**
 * ActivityDiaryTab — Audit Center → Activity Diary (BT Batch 2 P4).
 * Readers for two logs that were write-only for months:
 *   • audit_log — silent auto-rolls, negative-stock events, variance
 *     acceptances, incident resolutions, pickup/CBP events (D3)
 *   • request_approval_log — who confirmed which stock-request PO, when,
 *     via which PIN/TOTP (D10)
 */
import { useCallback, useEffect, useState } from 'react';
import { api } from '../../contexts/AuthContext';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { RefreshCw, BookOpen, ClipboardCheck, ChevronLeft, ChevronRight } from 'lucide-react';
import { fmtDateTime } from '../../lib/utils';

const PAGE = 50;

// Rough family → color so the diary scans visually.
const typeCls = (t = '') =>
  /loss|negative|shortage|variance|dispute|fail/.test(t) ? 'bg-rose-100 text-rose-700'
  : /resolved|accepted|verified|completed/.test(t) ? 'bg-emerald-100 text-emerald-700'
  : /created|sent|ordered/.test(t) ? 'bg-sky-100 text-sky-700'
  : 'bg-slate-100 text-slate-700';

export default function ActivityDiaryTab() {
  const { branches } = useAuth();
  const [view, setView] = useState('diary'); // diary | approvals
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);

  const [branchFilter, setBranchFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [search, setSearch] = useState('');

  const branchName = (id) => (branches || []).find(b => b.id === id)?.name || id || '—';

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { skip: page * PAGE, limit: PAGE };
      if (branchFilter !== 'all') params.branch_id = branchFilter;
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      if (view === 'diary') {
        if (typeFilter !== 'all') params.log_type = typeFilter;
        if (search.trim()) params.search = search.trim();
        const res = await api.get('/audit/activity-log', { params });
        setRows(res.data.rows || []);
        setTotal(res.data.total || 0);
        setTypes(res.data.types || []);
      } else {
        const res = await api.get('/audit/approval-log', { params });
        setRows(res.data.rows || []);
        setTotal(res.data.total || 0);
      }
    } catch { setRows([]); setTotal(0); }
    setLoading(false);
  }, [view, page, branchFilter, typeFilter, dateFrom, dateTo, search]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPage(0); }, [view, branchFilter, typeFilter, dateFrom, dateTo, search]);

  const pages = Math.max(1, Math.ceil(total / PAGE));

  return (
    <div className="space-y-4" data-testid="activity-diary-tab">
      {/* View switch */}
      <div className="flex gap-2">
        <Button size="sm" variant={view === 'diary' ? 'default' : 'outline'}
          className={`h-8 text-xs ${view === 'diary' ? 'bg-[#1A4D2E] text-white' : ''}`}
          onClick={() => setView('diary')} data-testid="diary-view-btn">
          <BookOpen size={13} className="mr-1" />Activity Diary
        </Button>
        <Button size="sm" variant={view === 'approvals' ? 'default' : 'outline'}
          className={`h-8 text-xs ${view === 'approvals' ? 'bg-[#1A4D2E] text-white' : ''}`}
          onClick={() => setView('approvals')} data-testid="approvals-view-btn">
          <ClipboardCheck size={13} className="mr-1" />Request Approvals
        </Button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-end gap-3">
        <div>
          <Label className="text-xs">Branch</Label>
          <Select value={branchFilter} onValueChange={setBranchFilter}>
            <SelectTrigger className="h-9 w-44" data-testid="diary-branch-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All branches</SelectItem>
              {(branches || []).map(b => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        {view === 'diary' && (
          <div>
            <Label className="text-xs">Event type</Label>
            <Select value={typeFilter} onValueChange={setTypeFilter}>
              <SelectTrigger className="h-9 w-56" data-testid="diary-type-filter"><SelectValue /></SelectTrigger>
              <SelectContent className="max-h-72">
                <SelectItem value="all">All types</SelectItem>
                {types.map(t => <SelectItem key={t} value={t}>{t.replace(/_/g, ' ')}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        )}
        <div>
          <Label className="text-xs">From</Label>
          <Input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} className="h-9 w-36" />
        </div>
        <div>
          <Label className="text-xs">To</Label>
          <Input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} className="h-9 w-36" />
        </div>
        {view === 'diary' && (
          <div>
            <Label className="text-xs">Search</Label>
            <Input value={search} onChange={e => setSearch(e.target.value)}
              placeholder="order number, product…" className="h-9 w-52" data-testid="diary-search" />
          </div>
        )}
        <Button variant="outline" size="sm" className="h-9" onClick={load} data-testid="diary-refresh">
          <RefreshCw size={13} className={loading ? 'animate-spin' : ''} />
        </Button>
      </div>

      {/* Table */}
      <Card>
        <CardContent className="p-0 overflow-x-auto">
          {view === 'diary' ? (
            <Table>
              <TableHeader><TableRow>
                <TableHead className="text-xs uppercase text-slate-500 w-40">When</TableHead>
                <TableHead className="text-xs uppercase text-slate-500">Event</TableHead>
                <TableHead className="text-xs uppercase text-slate-500">What happened</TableHead>
                <TableHead className="text-xs uppercase text-slate-500">Branch</TableHead>
                <TableHead className="text-xs uppercase text-slate-500">By</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow><TableCell colSpan={5} className="text-center text-sm text-slate-400 py-8">Loading…</TableCell></TableRow>
                ) : rows.length === 0 ? (
                  <TableRow><TableCell colSpan={5} className="text-center text-sm text-slate-400 py-8">
                    No diary entries match these filters.
                  </TableCell></TableRow>
                ) : rows.map(r => (
                  <TableRow key={r.id} data-testid={`diary-row-${r.id}`}>
                    <TableCell className="text-xs text-slate-500 whitespace-nowrap">{fmtDateTime(r.created_at)}</TableCell>
                    <TableCell><Badge className={`text-[10px] ${typeCls(r.type)}`}>{(r.type || '').replace(/_/g, ' ')}</Badge></TableCell>
                    <TableCell className="text-xs max-w-xl">{r.description}</TableCell>
                    <TableCell className="text-xs">{branchName(r.branch_id)}</TableCell>
                    <TableCell className="text-xs">{r.user_name || '—'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <Table>
              <TableHeader><TableRow>
                <TableHead className="text-xs uppercase text-slate-500 w-40">When</TableHead>
                <TableHead className="text-xs uppercase text-slate-500">PO</TableHead>
                <TableHead className="text-xs uppercase text-slate-500">Decision</TableHead>
                <TableHead className="text-xs uppercase text-slate-500">Route</TableHead>
                <TableHead className="text-xs uppercase text-slate-500">Approved by</TableHead>
                <TableHead className="text-xs uppercase text-slate-500">Method</TableHead>
                <TableHead className="text-xs uppercase text-slate-500">Note</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow><TableCell colSpan={7} className="text-center text-sm text-slate-400 py-8">Loading…</TableCell></TableRow>
                ) : rows.length === 0 ? (
                  <TableRow><TableCell colSpan={7} className="text-center text-sm text-slate-400 py-8">
                    No request approvals match these filters.
                  </TableCell></TableRow>
                ) : rows.map(r => (
                  <TableRow key={r.id} data-testid={`approval-row-${r.id}`}>
                    <TableCell className="text-xs text-slate-500 whitespace-nowrap">{fmtDateTime(r.created_at)}</TableCell>
                    <TableCell className="font-mono text-xs">{r.po_number}</TableCell>
                    <TableCell>
                      <Badge className={`text-[10px] ${r.approval_status === 'approved' ? 'bg-emerald-100 text-emerald-700'
                        : r.approval_status === 'rejected' ? 'bg-rose-100 text-rose-700'
                        : 'bg-amber-100 text-amber-800'}`}>{r.approval_status}</Badge>
                    </TableCell>
                    <TableCell className="text-xs">{branchName(r.supply_branch_id)} → {branchName(r.requesting_branch_id)}</TableCell>
                    <TableCell className="text-xs">{r.approved_by_name || r.caller_name || '—'}</TableCell>
                    <TableCell className="text-xs">{r.approval_method || '—'}</TableCell>
                    <TableCell className="text-xs max-w-xs truncate" title={r.approval_note}>{r.approval_note || '—'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Pager */}
      <div className="flex items-center justify-between text-xs text-slate-500">
        <span>{total} entr{total === 1 ? 'y' : 'ies'}</span>
        {pages > 1 && (
          <span className="flex items-center gap-2">
            <Button size="sm" variant="outline" className="h-7 px-2" disabled={page === 0}
              onClick={() => setPage(p => p - 1)}><ChevronLeft size={13} /></Button>
            Page {page + 1} of {pages}
            <Button size="sm" variant="outline" className="h-7 px-2" disabled={page >= pages - 1}
              onClick={() => setPage(p => p + 1)}><ChevronRight size={13} /></Button>
          </span>
        )}
      </div>
    </div>
  );
}
